/**
 * B2B Variant Auto-Select + Scroll Lock
 *
 * Two responsibilities:
 *
 * 1. SCROLL LOCK — Dawn's product-info.js calls element.focus() after every
 *    variant fetch, which causes the browser to scroll the focused pill into
 *    view (often jumping to the top). We intercept every `change` event on
 *    variant-selects and lock the scroll position for 900 ms — long enough
 *    to cover the full async fetch → DOM-replace → focus() cycle.
 *
 * 2. AUTO-SELECT — When option 1 changes, Dawn re-renders variant-selects.
 *    If the selected option 2 value is now disabled (unavailable combination),
 *    we automatically click the first available option 2 value.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. SCROLL LOCK                                                       */
  /* ------------------------------------------------------------------ */

  var _lockY = null;
  var _lockTimer = null;

  function _scrollGuard() {
    if (_lockY !== null && window.scrollY !== _lockY) {
      window.scrollTo({ top: _lockY, behavior: 'instant' });
    }
  }

  /**
   * Save the current scrollY and prevent any scroll change for durationMs.
   * Safe to call multiple times — refreshes the lock each time.
   */
  function lockScroll(durationMs) {
    _lockY = window.scrollY;

    if (_lockTimer === null) {
      window.addEventListener('scroll', _scrollGuard, { passive: true });
    } else {
      clearTimeout(_lockTimer);
    }

    _lockTimer = setTimeout(function () {
      window.removeEventListener('scroll', _scrollGuard);
      _lockY = null;
      _lockTimer = null;
    }, durationMs);
  }

  /* ------------------------------------------------------------------ */
  /* 2. AUTO-SELECT + OBSERVER                                            */
  /* ------------------------------------------------------------------ */

  function initVariantAutoSelect() {
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    var initialVS = productInfo.querySelector('variant-selects');
    if (!initialVS) return;

    // Lock scroll on EVERY option change, triggered by the user OR by our
    // programmatic click. Dawn fetches after each change and then calls
    // focus() which would otherwise scroll the page.
    productInfo.addEventListener('change', function (event) {
      if (event.target.closest('variant-selects')) {
        lockScroll(900);
      }
    });

    // Dawn's HTMLUpdateUtility.viewTransition inserts a brand-new
    // variant-selects element as a sibling and hides/removes the old one.
    // We observe the parent for that insertion to run the auto-fix.
    var vsParent = initialVS.parentNode;
    if (!vsParent) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeName === 'VARIANT-SELECTS') {
            var newVS = added[j];
            requestAnimationFrame(function () {
              fixUnavailableSelections(newVS);
            });
            return;
          }
        }
      }
    });

    observer.observe(vsParent, { childList: true });
  }

  /**
   * For every option group after the first, if the currently-checked
   * input is now disabled, click the first available alternative.
   */
  function fixUnavailableSelections(variantSelects) {
    var fieldsets = Array.from(
      variantSelects.querySelectorAll('fieldset.product-form__input--pill')
    );

    for (var i = 1; i < fieldsets.length; i++) {
      var fieldset = fieldsets[i];
      var checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (!checkedInput) continue;

      if (checkedInput.disabled || checkedInput.classList.contains('disabled')) {
        var firstAvailable = fieldset.querySelector(
          'input[type="radio"]:not([disabled]):not(.disabled)'
        );
        if (firstAvailable) {
          firstAvailable.click(); // triggers full Dawn update cycle;
          // scroll is already locked by the `change` listener above
          break;
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                 */
  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantAutoSelect);
  } else {
    initVariantAutoSelect();
  }
})();
